import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Home as HomeIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  Factory as FactoryIcon,
  CalendarToday as CalendarTodayIcon,
  ViewTimeline as ViewTimelineIcon,
  Settings as SettingsIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';

const drawerWidth = 260;

interface AppShellProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  matchPrefixes?: string[];
}

const navItems: NavItem[] = [
  {
    label: 'หน้าหลัก',
    path: '/',
    icon: <HomeIcon />,
  },
  {
    label: 'สต็อก JLC',
    path: '/inventory',
    icon: <InventoryIcon />,
    matchPrefixes: ['/inventory'],
  },
  {
    label: 'สต็อก TANLAB',
    path: '/tanlab',
    icon: <ScienceIcon />,
    matchPrefixes: ['/tanlab'],
  },
  {
    label: 'Dashboard Planning',
    path: '/dashboard-planning',
    icon: <AssessmentIcon />,
  },
  {
    label: 'แผนการผลิต',
    path: '/production-planning/monthly',
    icon: <FactoryIcon />,
    matchPrefixes: ['/production-planning', '/monthly-planning', '/daily-production'],
  },
  {
    label: 'ปฏิทินการผลิต',
    path: '/calendar',
    icon: <CalendarTodayIcon />,
    matchPrefixes: ['/calendar', '/production-timeline'],
  },
];

function getPageInfo(pathname: string) {
  if (pathname.startsWith('/calendar') || pathname.startsWith('/production-timeline')) {
    return {
      title: 'ปฏิทินการผลิต',
      subtitle: 'ดูภาพรวมและติดตามแผนการผลิตในรูปแบบปฏิทิน',
    };
  }

  if (pathname.startsWith('/dashboard-planning')) {
    return {
      title: 'Dashboard Planning',
      subtitle: 'ภาพรวมกระบวนการวางแผนการผลิตและสถานะแผนทั้งหมด',
    };
  }

  if (
    pathname.startsWith('/production-planning') ||
    pathname.startsWith('/monthly-planning') ||
    pathname.startsWith('/daily-production')
  ) {
    return {
      title: 'แผนการผลิต',
      subtitle: 'จัดการแผนการผลิต เชื่อมต่อข้อมูลสต็อกและยอดขาย',
    };
  }

  if (pathname.startsWith('/inventory')) {
    return {
      title: 'สต็อก JLC',
      subtitle: 'ระบบจัดการคลังสินค้า JLC',
    };
  }

  if (pathname.startsWith('/tanlab')) {
    return {
      title: 'สต็อก TANLAB',
      subtitle: 'ระบบจัดการคลังสินค้า TANLAB',
    };
  }

  if (pathname.startsWith('/stock-runway')) {
    return {
      title: 'Stock Runway',
      subtitle: 'คาดการณ์วันที่สต็อกจะหมดจากอัตราการใช้งานจริง',
    };
  }

  if (pathname.startsWith('/unit-converter')) {
    return {
      title: 'แปลงหน่วยสินค้า',
      subtitle: 'จัดการค่าหน่วยบรรจุและการแปลงหน่วยแบบรวมศูนย์',
    };
  }

  if (pathname.startsWith('/approval')) {
    return {
      title: 'อนุมัติแผนการผลิต',
      subtitle: 'ตรวจสอบและอนุมัติแผนการผลิตจากทุกฝ่าย',
    };
  }

  return {
    title: 'ภาพรวมสต็อก',
    subtitle: 'หน้าหลักของระบบจัดการคลังสินค้าและการผลิต',
  };
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const { title, subtitle } = getPageInfo(location.pathname);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.grey[100] }}>
      {/* Sidebar - fixed on the left for all pages */}
      <Box
        component="nav"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 3,
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: alpha('#fff', 0.12),
          }}
        >
          <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
            ระบบจัดการคลังสินค้า
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Stock Dashboard
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
          <List sx={{ px: 1 }}>
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.path ||
                item.matchPrefixes?.some((prefix) => location.pathname.startsWith(prefix));

              return (
                <ListItemButton
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    mb: 0.5,
                    borderRadius: 2,
                    px: 2,
                    py: 1.2,
                    '&.Mui-selected': {
                      bgcolor: alpha('#ffffff', 0.16),
                      '&:hover': { bgcolor: alpha('#ffffff', 0.24) },
                    },
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.08),
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: isActive ? 700 : 500, fontSize: '0.95rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: alpha('#fff', 0.16),
          }}
        >
          <Chip
            icon={<ViewTimelineIcon sx={{ fontSize: 18 }} />}
            label="Production Timeline"
            size="small"
            clickable
            component={RouterLink as React.ElementType}
            to="/calendar"
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              bgcolor: alpha('#ffffff', 0.08),
              color: 'common.white',
              '&:hover': { bgcolor: alpha('#ffffff', 0.16) },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, opacity: 0.8 }}>
            <SettingsIcon sx={{ fontSize: 18, mr: 1 }} />
            <Typography variant="caption">ระบบเวอร์ชันทดสอบ (Local)</Typography>
          </Box>
        </Box>
      </Box>

      {/* Main area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, ml: `${drawerWidth}px` }}>
        {/* Top header (gradient) - Sticky */}
        <Box
          component="header"
          sx={{
            px: 4,
            py: 2,
            bgcolor: theme.palette.grey[100],
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              px: 4,
              py: 3,
              color: 'common.white',
              boxShadow: 3,
              background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 45%, #ec4899 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {subtitle}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={location.pathname}
              sx={{
                bgcolor: alpha('#ffffff', 0.18),
                color: 'common.white',
                borderRadius: 999,
                fontWeight: 500,
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: 4,
            pb: 4,
            pt: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
